import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { Link, useNavigate } from "@tanstack/react-router";
import { useEffect, useId, useRef, useState } from "react";

import { directoryPageQueryOptions, useRelatedParties } from "../../api/directory";
import { useSession } from "../../api/session";
import {
	DIRECTORY_QUERY_MAX_LENGTH,
	PARTY_KIND_DOT_CLASS,
	PARTY_KIND_LABELS,
	type DirectoryEntry,
} from "../../domain/directory";
import { cn } from "../../lib/cn";
import { PartyLink } from "../../routes/directory/-components/party-link";
import { SearchIcon } from "../icons";
import { Card, cardActionClassName, CardHeader } from "../ui/card";
import { Skeleton } from "../ui/skeleton";

/** Whether a key pressed on `target` is typed into it, which is where "/" is a character rather than the way to search. */
function takesText(target: EventTarget | null): boolean {
	return (
		target instanceof HTMLInputElement ||
		target instanceof HTMLTextAreaElement ||
		target instanceof HTMLSelectElement ||
		(target instanceof HTMLElement && target.isContentEditable)
	);
}

interface FoundPartiesProps {
	readonly label: string;
	readonly parties: readonly DirectoryEntry[];
	/** Called as a party's link is followed: the dropdown closes and the box empties. */
	readonly onChoose: () => void;
}

/**
 * Parties as the dropdown lists them: the whole row links to the party's page — the kind's dot, the name, the line the
 * Directory shows under it, and the kind.
 */
function FoundParties({ label, parties, onChoose }: FoundPartiesProps) {
	return (
		<ul aria-label={label}>
			{parties.map(party => (
				<li key={`${party.kind}#${party.id}`} className="border-b border-line last:border-0">
					<PartyLink
						kind={party.kind}
						id={party.id}
						className="flex items-start gap-2.5 px-4 py-2.5 -outline-offset-2 transition-colors duration-[120ms] hover:bg-hover-tint focus-visible:bg-hover-tint"
						onClick={onChoose}
					>
						<span
							aria-hidden="true"
							className={cn(
								"mt-[5px] h-[7px] w-[7px] shrink-0 rounded-full",
								PARTY_KIND_DOT_CLASS[party.kind],
							)}
						/>
						<span className="min-w-0 flex-1">
							<span className="block truncate text-ctl-lg font-medium text-ink">{party.name}</span>
							<span className="block truncate text-caption text-ink-3">{party.detail}</span>
						</span>
						<span className="shrink-0 text-caption text-ink-3">{PARTY_KIND_LABELS[party.kind]}</span>
					</PartyLink>
				</li>
			))}
		</ul>
	);
}

interface SearchResultsProps {
	/** What the box holds, trimmed: never empty while the dropdown shows. */
	readonly q: string;
	readonly onChoose: () => void;
}

/**
 * The Directory's own search, as its page answers it: the first page of the parties whose names hold the search, A→Z,
 * and then — once the box has rested, where those end — the parties the Directory's knowledge base finds most like it.
 * The requests are the page's own (`directoryPageQueryOptions`, `useRelatedParties`), so the two share their answers
 * and never disagree; See all opens the page on the same search, with its kind pills and the rest of the pages.
 */
function SearchResults({ q, onChoose }: SearchResultsProps) {
	// The list follows every keystroke, as the page's does, and keeps the last answer up until the next one comes, so
	// the dropdown never empties between two keys.
	const {
		data: found,
		error,
		isPlaceholderData,
	} = useQuery({
		...directoryPageQueryOptions({ q }),
		placeholderData: keepPreviousData,
	});
	const related = useRelatedParties({
		q,
		namesEnd: found !== undefined && !isPlaceholderData && found.next === null,
	});

	return (
		<>
			<CardHeader
				title={found === undefined ? "Directory" : `${found.total} ${found.total === 1 ? "party" : "parties"}`}
				action={
					<Link to="/directory" search={{ q }} className={cardActionClassName} onClick={onChoose}>
						See all →
					</Link>
				}
			/>
			{found === undefined ? (
				error === null ? (
					<div role="status" className="flex flex-col gap-2.5 px-4 py-3">
						<span className="sr-only">Loading…</span>
						<Skeleton className="h-4 w-2/3" />
						<Skeleton className="h-4 w-1/2" />
					</div>
				) : (
					<p role="alert" className="px-4 py-3 text-caption text-alert">
						{error.message}
					</p>
				)
			) : found.total === 0 ? (
				<p className="px-4 py-3 text-caption text-ink-3">No party&apos;s name holds your search.</p>
			) : (
				<FoundParties label="Parties" parties={found.parties} onChoose={onChoose} />
			)}
			{related.length === 0 ? null : (
				<>
					<div className="border-t border-line">
						<CardHeader title="Related" />
					</div>
					<FoundParties label="Related parties" parties={related} onChoose={onChoose} />
				</>
			)}
		</>
	);
}

/**
 * The rail's search box, which searches the Directory: what it holds drops down under it in a card over the page —
 * the parties whose names hold it, then the related ones — each linking to its party's page. It is not a modal
 * dialog, for the reason the bell's menu is not (`notifications-menu.tsx`): it closes on Escape, on a press anywhere
 * else and when focus leaves it, and "/" typed anywhere but in a field of its own puts the cursor in it. Enter opens
 * the Directory page on the search. A member the organization has given no access to the Directory is told so in the
 * dropdown, and nothing is asked.
 */
export function SidebarSearch() {
	const { member } = useSession();
	const canSearch = member.access.directory !== "none";
	const navigate = useNavigate();
	const [text, setText] = useState("");
	const [isOpen, setIsOpen] = useState(false);
	const q = text.trim();
	const isShowing = isOpen && q !== "";
	const searchRef = useRef<HTMLDivElement>(null);
	const inputRef = useRef<HTMLInputElement>(null);
	const panelId = useId();

	/** Once a result, See all or Enter has taken the member to the page they chose, the box empties, which closes it. */
	const done = () => {
		setText("");
	};

	useEffect(() => {
		const onKeyDown = (event: KeyboardEvent) => {
			if (event.key !== "/" || event.metaKey || event.ctrlKey || event.altKey || takesText(event.target)) {
				return;
			}
			event.preventDefault();
			inputRef.current?.focus();
		};
		document.addEventListener("keydown", onKeyDown);
		return () => {
			document.removeEventListener("keydown", onKeyDown);
		};
	}, []);

	useEffect(() => {
		if (!isShowing) {
			return;
		}
		const isOutside = (target: EventTarget | null) =>
			target instanceof Node && searchRef.current?.contains(target) !== true;
		const onPointerDown = (event: PointerEvent) => {
			if (isOutside(event.target)) {
				setIsOpen(false);
			}
		};
		const onFocusIn = (event: FocusEvent) => {
			if (isOutside(event.target)) {
				setIsOpen(false);
			}
		};
		const onKeyDown = (event: KeyboardEvent) => {
			if (event.key === "Escape") {
				// Focus first: the box opens the dropdown as it gets focus, and Escape is what closes it.
				inputRef.current?.focus();
				setIsOpen(false);
				return;
			}
			if ((event.key !== "ArrowDown" && event.key !== "ArrowUp") || isOutside(event.target)) {
				return;
			}
			// The arrows walk the box and the parties under it, in the order they are listed.
			const stops = [inputRef.current, ...(searchRef.current?.querySelectorAll(":scope li a") ?? [])];
			const at = stops.indexOf(document.activeElement);
			const next = stops.at(event.key === "ArrowDown" ? Math.min(at + 1, stops.length - 1) : Math.max(at - 1, 0));
			event.preventDefault();
			if (next instanceof HTMLElement) {
				next.focus();
			}
		};
		document.addEventListener("pointerdown", onPointerDown);
		document.addEventListener("focusin", onFocusIn);
		document.addEventListener("keydown", onKeyDown);
		return () => {
			document.removeEventListener("pointerdown", onPointerDown);
			document.removeEventListener("focusin", onFocusIn);
			document.removeEventListener("keydown", onKeyDown);
		};
	}, [isShowing]);

	return (
		<div ref={searchRef} className="relative mx-3 mt-0.5 mb-2">
			<div className="group flex items-center gap-[7px] rounded-field bg-white/[0.06] px-2.5 py-[7px] transition-colors focus-within:bg-white/[0.09] hover:bg-white/[0.09]">
				<SearchIcon
					className="h-3 w-3 shrink-0 fill-none stroke-[#FAF8F3]/45 stroke-[1.8]"
					strokeLinecap="round"
				/>
				<input
					ref={inputRef}
					aria-label="Search the Directory"
					aria-keyshortcuts="/"
					aria-controls={isShowing ? panelId : undefined}
					placeholder="Search the Directory"
					autoComplete="off"
					// As long a search as the API takes, so the box never asks it something it answers with 400.
					maxLength={DIRECTORY_QUERY_MAX_LENGTH}
					value={text}
					onChange={event => {
						setText(event.target.value);
						setIsOpen(true);
					}}
					onFocus={() => {
						setIsOpen(true);
					}}
					onKeyDown={event => {
						if (!isOpen && event.key === "ArrowDown") {
							event.preventDefault();
							setIsOpen(true);
						} else if (canSearch && q !== "" && event.key === "Enter") {
							event.preventDefault();
							void navigate({ to: "/directory", search: { q } });
							done();
						}
					}}
					className="min-w-0 flex-1 border-0 bg-transparent text-ctl text-[#FAF8F3]/85 outline-none placeholder:text-[#FAF8F3]/40"
				/>
				<kbd aria-hidden="true" className="text-micro text-white/35 group-focus-within:hidden">
					/
				</kbd>
			</div>
			{isShowing ? (
				<Card id={panelId} className="absolute top-full left-0 z-30 mt-1.5 w-[360px] text-ink">
					{/* The shell never scrolls, so a dropdown taller than the window scrolls itself rather than run off it. */}
					<div className="thin-scroll max-h-[calc(100dvh-8rem)] overflow-y-auto">
						{canSearch ? (
							<SearchResults q={q} onChoose={done} />
						) : (
							<p className="px-4 py-3 text-caption text-ink-3">
								You do not have access to the Directory. Ask an owner or an admin for it.
							</p>
						)}
					</div>
				</Card>
			) : null}
		</div>
	);
}
